import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuthContext } from '../context/AuthContext';
import { IoSaveOutline, IoRefreshOutline, IoMailOutline } from 'react-icons/io5';

const TYPE_LABELS = {
  workout: { label: 'Entrenamiento 🏋️', badge: 'bg-red-900/40 text-red-300 border-red-700/40' },
  measurement: { label: 'Medición de progreso 📊', badge: 'bg-blue-900/40 text-blue-300 border-blue-700/40' },
};

const TemplateCard = ({ tpl, onSaved }) => {
  const [form, setForm] = useState({
    subject: tpl.subject,
    title: tpl.title,
    intro: tpl.intro,
    cta_label: tpl.cta_label,
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const dirty =
    form.subject !== tpl.subject ||
    form.title !== tpl.title ||
    form.intro !== tpl.intro ||
    form.cta_label !== tpl.cta_label;

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/coach/reminder-templates/${tpl.reminder_type}`, form);
      toast.success('Plantilla guardada.');
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const res = await api.post(`/coach/reminder-templates/${tpl.reminder_type}/reset`);
      toast.success('Plantilla restablecida.');
      onSaved(res.data);
      setForm({
        subject: res.data.subject,
        title: res.data.title,
        intro: res.data.intro,
        cta_label: res.data.cta_label,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al restablecer.');
    } finally {
      setResetting(false);
    }
  };

  const meta = TYPE_LABELS[tpl.reminder_type] || { label: tpl.reminder_type, badge: 'bg-gray-700 text-gray-300 border-gray-600' };

  return (
    <div className="glass-panel p-6 rounded-xl space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${meta.badge}`}>
          {meta.label}
        </span>
        <p className="text-xs text-gray-500">
          Última edición: {new Date(tpl.updated_at).toLocaleString('es-ES')}
        </p>
      </div>

      <div className="space-y-3">
        <label className="block">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Asunto del correo</span>
          <input
            type="text"
            value={form.subject}
            onChange={set('subject')}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
          />
        </label>

        <label className="block">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Título en el correo</span>
          <input
            type="text"
            value={form.title}
            onChange={set('title')}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
          />
        </label>

        <label className="block">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Cuerpo del mensaje</span>
          <textarea
            rows={3}
            value={form.intro}
            onChange={set('intro')}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500 resize-y"
          />
        </label>

        <label className="block">
          <span className="text-xs text-gray-400 uppercase tracking-wide">Texto del botón CTA</span>
          <input
            type="text"
            value={form.cta_label}
            onChange={set('cta_label')}
            className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500"
          />
        </label>
      </div>

      {/* Preview strip */}
      <div className="rounded-lg border border-white/10 bg-white/3 p-4 space-y-1">
        <p className="text-xs text-gray-500 uppercase tracking-wide">Vista previa</p>
        <p className="text-xs text-gray-400">
          <span className="text-gray-500">Asunto:</span> {form.subject}
        </p>
        <p className="text-white font-semibold text-sm">{form.title}</p>
        <p className="text-gray-400 text-sm leading-relaxed">{form.intro}</p>
        <span className="inline-block mt-1 bg-red-600 text-white text-xs font-bold px-4 py-1.5 rounded-lg">
          {form.cta_label}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 bg-marizan-red hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {saving ? <IoRefreshOutline className="animate-spin" size={15} /> : <IoSaveOutline size={15} />}
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
        <button
          type="button"
          onClick={reset}
          disabled={resetting}
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-gray-300 text-sm px-4 py-2 rounded-lg transition-colors"
        >
          {resetting ? <IoRefreshOutline className="animate-spin" size={15} /> : <IoRefreshOutline size={15} />}
          Restablecer predeterminado
        </button>
      </div>
    </div>
  );
};

const ReminderTemplates = () => {
  const { user } = useAuthContext();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/coach/reminder-templates')
      .then((r) => setTemplates(r.data || []))
      .catch(() => toast.error('No se pudieron cargar las plantillas.'))
      .finally(() => setLoading(false));
  }, []);

  if (user?.role !== 'coach') {
    return (
      <div className="glass-panel p-6 rounded-xl">
        <p className="text-white font-semibold">Acceso restringido</p>
        <p className="text-sm text-gray-400 mt-1">Las plantillas de recordatorios son exclusivas del coach.</p>
      </div>
    );
  }

  const handleSaved = (updated) => {
    setTemplates((prev) => prev.map((t) => (t.reminder_type === updated.reminder_type ? updated : t)));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => <div key={i} className="skeleton h-64 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="text-3xl font-display font-bold text-white flex items-center gap-3">
          <IoMailOutline className="text-red-400" />
          Plantillas de Recordatorios
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Personaliza el asunto, título, cuerpo y botón de cada correo de recordatorio automático.
        </p>
      </div>

      {templates.length === 0 ? (
        <p className="text-gray-400">No se encontraron plantillas.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((tpl) => (
            <TemplateCard key={tpl.reminder_type} tpl={tpl} onSaved={handleSaved} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReminderTemplates;
